/**
 * Cliente HTTP do painel (RNF15 / RNF12 / RNF19).
 *
 * Decisoes estruturais:
 *
 * 1. O TENANT NAO VIAJA NA REQUISICAO. A sessao (cookie HttpOnly, mesma origem)
 *    e quem diz de que empresa o usuario e; o servidor ignora qualquer
 *    `companyId` vindo do navegador. A UNICA excecao e a equipe do Converte
 *    Facil (`platform_admin`): `definirEmpresaEmFoco` faz toda requisicao levar
 *    `X-Company-Id` com a empresa escolhida no seletor, e o SERVIDOR confere o
 *    papel da sessao e se a empresa existe e esta ativa (src/api/tenant.ts).
 *    Para qualquer outro papel o servidor ignora o cabecalho -- e este arquivo
 *    nem o envia, porque so o CompanyProvider da equipe liga o foco.
 *
 * 2. Carregando e erro sao ESTADO, nao excecao solta. `AsyncState` e uma uniao
 *    discriminada: renderizar dado sem antes tratar os outros dois casos nao
 *    compila. E o que impede a tela branca com spinner eterno.
 *
 * 3. 401 E UM EVENTO GLOBAL. Qualquer resposta 401 avisa os assinantes
 *    (`onUnauthorized`); o provedor de autenticacao ouve e leva para o login.
 *    Sem isso, uma sessao vencida viraria seis cartoes com "sem permissao".
 *
 * 4. Modo simulado (RNF19) SOMENTE por `VITE_USE_MOCK_DATA=true`. O fallback
 *    silencioso "se o servidor nao respondeu em DEV, mostra numero falso" foi
 *    removido: um R$ 21 mil ficticio na tela, mesmo com a faixa de aviso, e a
 *    coisa mais perigosa que um painel de faturamento pode mostrar.
 *
 * 5. Toda requisicao que muda estado leva `X-Requested-With`. E a segunda
 *    tranca contra CSRF do servidor (a primeira e `SameSite=Strict`).
 *
 * Sem biblioteca de dados: `fetch` + um hook resolvem este escopo.
 */

import { useCallback, useEffect, useState } from 'react';
import type {
  CampaignListItem,
  DailySeries,
  DashboardMetrics,
  LeadListItem,
  Page,
  PeriodRange,
  SessionInfo,
} from '../types';

// -----------------------------------------------------------------------------
// Ambiente
// -----------------------------------------------------------------------------

function readEnv(name: string): string | undefined {
  const bag = import.meta.env as unknown as Record<string, unknown>;
  const value = bag[name];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Ligado por VITE_USE_MOCK_DATA=true em frontend/.env.local (nao versionado). */
export const MOCK_FORCED = readEnv('VITE_USE_MOCK_DATA') === 'true';

/** Mesmo valor que `XHR_HEADER_VALUE` em src/app.ts. */
const XHR_HEADER = 'ConverteFacil';

// -----------------------------------------------------------------------------
// Erro
// -----------------------------------------------------------------------------

/**
 * Falha de API ja traduzida para o operador.
 *
 * `status === 0` significa que a requisicao nem chegou ao servidor (rede caiu,
 * backend fora do ar). `code` e o codigo curto que o backend manda no JSON.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly path: string;
  public readonly code: string | null;
  public readonly retryAfterSeconds: number | null;

  constructor(message: string, status: number, path: string, code: string | null = null, retryAfterSeconds: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.path = path;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Mensagem pronta para a tela, sem jargao de HTTP quando da para evitar. */
  public get userMessage(): string {
    if (this.status === 0) {
      return 'Não foi possível falar com o servidor. Verifique se o motor de recuperação está no ar.';
    }
    if (this.status === 401) {
      return 'Sua sessão expirou. Entre de novo para continuar.';
    }
    if (this.status === 403) {
      return 'Sem permissão para ver os dados desta empresa.';
    }
    if (this.status === 404 && this.code === 'empresa_nao_encontrada') {
      return 'Esta empresa está pausada ou não existe mais. Escolha outra empresa no seletor do cabeçalho.';
    }
    if (this.status === 404) {
      return 'Este recurso ainda não existe no servidor.';
    }
    if (this.status === 429) {
      return 'Muitas tentativas em pouco tempo. Aguarde um instante e tente de novo.';
    }
    if (this.status >= 500) {
      return 'O servidor falhou ao responder. Tente novamente em instantes.';
    }
    return this.message;
  }
}

// -----------------------------------------------------------------------------
// 401 global
// -----------------------------------------------------------------------------

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Assina o evento "sessao invalida". Devolve a funcao que cancela. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

function emitUnauthorized(): void {
  for (const listener of unauthorizedListeners) {
    listener();
  }
}

// -----------------------------------------------------------------------------
// Transporte
// -----------------------------------------------------------------------------

const REQUEST_TIMEOUT_MS = 10_000;

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | null | undefined>>;
  readonly signal?: AbortSignal;
  /** 401 nesta chamada NAO dispara o evento global (usado por /me e /login). */
  readonly silent401?: boolean;
}

/**
 * Empresa em foco da equipe (`platform_admin`), enviada em `X-Company-Id`.
 * `null` = nenhuma (lojista, modo simulado, ou a lista ainda nao carregou).
 * Estado de modulo e nao de contexto React: todo fetcher do painel passa por
 * `request`, e o cabecalho precisa valer para todos sem mudar a assinatura de
 * nenhum deles.
 */
let empresaEmFoco: string | null = null;

/** Chamado SO pelo CompanyProvider da equipe (lib/companyContext.tsx). */
export function definirEmpresaEmFoco(companyId: string | null): void {
  empresaEmFoco = companyId;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, value);
    }
  }

  const timeout = new AbortController();
  const timer = window.setTimeout(() => {
    timeout.abort();
  }, REQUEST_TIMEOUT_MS);
  const external = options.signal;
  const onExternalAbort = (): void => {
    timeout.abort();
  };
  external?.addEventListener('abort', onExternalAbort);

  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') {
    headers['X-Requested-With'] = XHR_HEADER;
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (empresaEmFoco !== null) {
    headers['X-Company-Id'] = empresaEmFoco;
  }

  try {
    const response = await fetch(url.toString(), {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: 'same-origin',
      signal: timeout.signal,
    });

    if (!response.ok) {
      let code: string | null = null;
      try {
        const payload: unknown = await response.json();
        if (typeof payload === 'object' && payload !== null && typeof (payload as Record<string, unknown>)['code'] === 'string') {
          code = (payload as Record<string, string>)['code'] ?? null;
        }
      } catch {
        // Corpo nao-JSON (proxy, pagina de erro): fica so o status.
      }
      const retryAfter = Number(response.headers.get('retry-after'));
      if (response.status === 401 && options.silent401 !== true) {
        emitUnauthorized();
      }
      throw new ApiError(
        `Resposta ${response.status} em ${path}`,
        response.status,
        path,
        code,
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    // Abort pedido por quem chamou sobe como esta: nao e falha, e desmontagem.
    if (external?.aborted === true) {
      throw error;
    }
    throw new ApiError(descreverFalhaDeRede(error), 0, path);
  } finally {
    external?.removeEventListener('abort', onExternalAbort);
    window.clearTimeout(timer);
  }
}

function descreverFalhaDeRede(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'Tempo esgotado ao falar com o servidor.';
  }
  return 'Falha de rede ao falar com o servidor.';
}

// -----------------------------------------------------------------------------
// Sessao
// -----------------------------------------------------------------------------

export async function fetchSession(signal?: AbortSignal): Promise<SessionInfo | null> {
  if (MOCK_FORCED) {
    await atrasoSimulado(signal);
    return SESSAO_SIMULADA;
  }
  try {
    return await request<SessionInfo>('/api/auth/me', { signal, silent401: true });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null;
    }
    throw error;
  }
}

export async function login(email: string, password: string): Promise<SessionInfo> {
  if (MOCK_FORCED) {
    await atrasoSimulado();
    return SESSAO_SIMULADA;
  }
  return request<SessionInfo>('/api/auth/login', {
    method: 'POST',
    body: { email, password },
    silent401: true,
  });
}

export async function logout(): Promise<void> {
  if (MOCK_FORCED) {
    return;
  }
  await request<void>('/api/auth/logout', { method: 'POST', silent401: true });
}

// -----------------------------------------------------------------------------
// Endpoints de leitura
// -----------------------------------------------------------------------------

/** Resposta com a procedencia do dado colada nela ate a camada de apresentacao. */
export interface Envelope<T> {
  readonly data: T;
  /** `true` quando veio do gerador local, e nao do motor de recuperacao. */
  readonly simulated: boolean;
}

export function rangeQuery(range: PeriodRange): Record<string, string | null> {
  return { from: range.from, to: range.to };
}

export async function fetchOrSimulate<T>(
  path: string,
  query: Record<string, string | null | undefined>,
  signal: AbortSignal,
  simulate: () => T,
): Promise<Envelope<T>> {
  if (MOCK_FORCED) {
    await atrasoSimulado(signal);
    return { data: simulate(), simulated: true };
  }
  return { data: await request<T>(path, { query, signal }), simulated: false };
}

export function fetchMetrics(range: PeriodRange, signal: AbortSignal): Promise<Envelope<DashboardMetrics>> {
  return fetchOrSimulate('/api/metrics', rangeQuery(range), signal, () => simularMetricas(range));
}

export function fetchDaily(range: PeriodRange, signal: AbortSignal): Promise<Envelope<DailySeries>> {
  return fetchOrSimulate('/api/metrics/daily', rangeQuery(range), signal, () => simularSerie(range));
}

export interface PageQuery {
  readonly limit: number;
  readonly offset: number;
}

export function fetchLeads(
  range: PeriodRange,
  page: PageQuery,
  signal: AbortSignal,
): Promise<Envelope<Page<LeadListItem>>> {
  return fetchOrSimulate(
    '/api/leads',
    { ...rangeQuery(range), limit: String(page.limit), offset: String(page.offset) },
    signal,
    () => simularLeads(page),
  );
}

export function fetchCampaigns(
  range: PeriodRange,
  page: PageQuery,
  signal: AbortSignal,
): Promise<Envelope<Page<CampaignListItem>>> {
  return fetchOrSimulate(
    '/api/campaigns',
    { ...rangeQuery(range), limit: String(page.limit), offset: String(page.offset) },
    signal,
    () => simularCampanhas(page),
  );
}

// -----------------------------------------------------------------------------
// Hook de recurso
// -----------------------------------------------------------------------------

export type AsyncState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly error: ApiError }
  | { readonly status: 'success'; readonly data: T; readonly simulated: boolean; readonly refreshing: boolean };

export interface AsyncResource<T> {
  readonly state: AsyncState<T>;
  /** Refaz a busca. Identidade estavel: pode ir direto no onClick. */
  readonly reload: () => void;
}

/**
 * Assina um recurso da API com ciclo de vida completo.
 *
 * `fetcher` PRECISA ter identidade estavel (useCallback no chamador): ele entra
 * na lista de dependencias do efeito e uma arrow inline dispararia uma
 * requisicao nova a cada render. Trocar o periodo troca o fetcher, e isso
 * recarrega -- MANTENDO o dado anterior na tela ate o novo chegar
 * (`refreshing: true`): esqueleto so na primeira carga, nunca no refetch.
 */
export function useApiResource<T>(
  fetcher: (signal: AbortSignal) => Promise<Envelope<T>>,
  refreshMs?: number,
): AsyncResource<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let ativo = true;

    setState((anterior) =>
      anterior.status === 'success' ? { ...anterior, refreshing: true } : { status: 'loading' },
    );

    fetcher(controller.signal)
      .then((envelope) => {
        if (ativo) {
          setState({ status: 'success', data: envelope.data, simulated: envelope.simulated, refreshing: false });
        }
      })
      .catch((error: unknown) => {
        if (!ativo || controller.signal.aborted) {
          return;
        }
        setState({
          status: 'error',
          error:
            error instanceof ApiError
              ? error
              : new ApiError('Falha inesperada ao carregar os dados.', 0, 'desconhecido'),
        });
      });

    return () => {
      ativo = false;
      controller.abort();
    };
  }, [fetcher, tentativa]);

  const reload = useCallback((): void => {
    setTentativa((valor) => valor + 1);
  }, []);

  useEffect(() => {
    if (refreshMs === undefined || refreshMs <= 0) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      // Aba escondida nao atualiza: um painel deixado aberto a noite inteira
      // numa aba de fundo geraria milhares de requisicoes inuteis.
      if (document.visibilityState === 'visible') {
        setTentativa((valor) => valor + 1);
      }
    }, refreshMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [refreshMs]);

  return { state, reload };
}

// =============================================================================
// DADOS SIMULADOS (RNF19) -- NAO E PRODUCAO
//
// Servidos SOMENTE com VITE_USE_MOCK_DATA=true. Regras que mantem isso
// inconfundivel:
//   - nenhum Math.random: a mesma tela sempre mostra os mesmos numeros;
//   - todo id comeca com "SIMULADO-";
//   - nomes e telefones sao ficticios;
//   - as gravacoes sao silencio gerado localmente, sem requisicao externa.
// =============================================================================

export const AGORA_SIMULADO = new Date('2026-09-24T12:00:00.000Z');

const SESSAO_SIMULADA: SessionInfo = {
  user: { email: 'demo@simulado.test', name: 'Equipe Converte Fácil', role: 'platform_admin' },
  company: { id: 'SIMULADO-empresa-demo', name: 'Escola Fluente', feeBps: 1500 },
  expiresAt: new Date(AGORA_SIMULADO.getTime() + 12 * 3600_000).toISOString(),
};

export function menosMinutos(minutos: number): string {
  return new Date(AGORA_SIMULADO.getTime() - minutos * 60_000).toISOString();
}

export function atrasoSimulado(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, 220);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

/**
 * Empresa em foco na previa: a mesma que o CompanyProvider guarda no navegador.
 * Ler daqui (e nao receber por parametro) mantem a assinatura dos fetchers igual
 * a da API real, em que o tenant vem da sessao e nao viaja na requisicao.
 */
function empresaSimuladaEmFoco(): { readonly id: string; readonly name: string; readonly feeBps: number; readonly fator: number } {
  let id = SESSAO_SIMULADA.company.id;
  try {
    id = window.localStorage.getItem('cf.empresa') ?? id;
  } catch {
    // sem armazenamento: fica a empresa da sessao
  }
  const FATORES: Readonly<Record<string, { readonly name: string; readonly feeBps: number; readonly fator: number }>> = {
    'SIMULADO-empresa-demo': { name: 'Escola Fluente', feeBps: 1_500, fator: 100 },
    'SIMULADO-empresa-fit': { name: 'FitPro Treinos', feeBps: 2_000, fator: 62 },
    'SIMULADO-empresa-dev': { name: 'CodeLab Cursos', feeBps: 1_500, fator: 38 },
    'SIMULADO-empresa-bem': { name: 'Bem Viver Terapias', feeBps: 1_800, fator: 12 },
  };
  const f = FATORES[id] ?? FATORES['SIMULADO-empresa-demo'] ?? { name: SESSAO_SIMULADA.company.name, feeBps: 1_500, fator: 100 };
  // Comissao alterada pela administracao na previa (cf.empresas.ajustes) vale
  // tambem aqui, para os KPIs simulados baterem com o cadastro.
  let feeBps = f.feeBps;
  try {
    const raw = window.localStorage.getItem('cf.empresas.ajustes');
    const ajustes: unknown = raw === null ? null : JSON.parse(raw);
    if (typeof ajustes === 'object' && ajustes !== null) {
      const patch = (ajustes as Record<string, { feeBps?: unknown }>)[id];
      if (patch !== undefined && typeof patch.feeBps === 'number' && Number.isInteger(patch.feeBps)) {
        feeBps = patch.feeBps;
      }
    }
  } catch {
    // sem armazenamento ou JSON torto: fica o catalogo
  }
  return { id, name: f.name, feeBps, fator: f.fator };
}

/** Dias cobertos pela janela; `tudo` vale 30 na previa. */
function diasDaJanela(range: PeriodRange): number {
  if (range.from === null || range.to === null) {
    return 30;
  }
  return Math.max(1, Math.min(365, Math.round((new Date(range.to).getTime() - new Date(range.from).getTime()) / 86_400_000)));
}

/** Escala inteira e deterministica: base (em 30 dias, empresa 100%) -> janela e empresa. */
function escalar(base: number, dias: number, fator: number): number {
  return Math.round((base * dias * fator) / (30 * 100));
}

function simularMetricas(range: PeriodRange): DashboardMetrics {
  const empresa = empresaSimuladaEmFoco();
  const dias = diasDaJanela(range);
  const abandonedCount = escalar(412, dias, empresa.fator);
  const voiceCalls = escalar(389, dias, empresa.fator);
  const voiceAnswered = escalar(231, dias, empresa.fator);
  const recoveredCount = escalar(87, dias, empresa.fator);
  const recoveredAmountCents = escalar(2_154_390, dias, empresa.fator);
  const commissionCents = Math.round((recoveredAmountCents * empresa.feeBps) / 10_000);
  return {
    companyId: empresa.id,
    companyName: empresa.name,
    abandonedCount,
    recoveredCount,
    recoveredAmountCents,
    abandonedAmountCents: escalar(10_298_700, dias, empresa.fator),
    commissionCents,
    commissionFeeBps: empresa.feeBps,
    conversionRateBps: abandonedCount === 0 ? 0 : Math.round((recoveredCount * 10_000) / abandonedCount),
    voiceCalls,
    voiceAnswered,
    whatsappSent: escalar(158, dias, empresa.fator),
    contactedCount: escalar(401, dias, empresa.fator),
    reversedCount: escalar(3, dias, empresa.fator),
    reversedAmountCents: escalar(74_700, dias, empresa.fator),
    periodStart: range.from ?? menosMinutos(60 * 24 * 30),
    periodEnd: range.to ?? AGORA_SIMULADO.toISOString(),
  };
}

function simularSerie(range: PeriodRange): DailySeries {
  const to = range.to === null ? AGORA_SIMULADO : new Date(range.to);
  const from = range.from === null ? new Date(to.getTime() - 30 * 86_400_000) : new Date(range.from);
  const dias = Math.max(1, Math.min(120, Math.round((to.getTime() - from.getTime()) / 86_400_000)));
  const points = Array.from({ length: dias }, (_, i) => {
    const dia = new Date(from.getTime() + i * 86_400_000);
    // Onda deterministica: fim de semana mais fraco, meio da semana mais forte.
    const semana = dia.getUTCDay();
    const fator = empresaSimuladaEmFoco().fator;
    const base = Math.max(1, Math.round(((semana === 0 || semana === 6 ? 6 : 14 + ((i * 7) % 9)) * fator) / 100));
    const recuperados = Math.round(base * 0.21);
    return {
      day: dia.toISOString().slice(0, 10),
      abandonedCount: base,
      abandonedAmountCents: base * 24_900,
      contactedCount: Math.round(base * 0.95),
      voiceCalls: Math.round(base * 0.94),
      whatsappSent: Math.round(base * 0.38),
      recoveredCount: recuperados,
      recoveredAmountCents: recuperados * 24_900,
    };
  });
  return {
    companyId: empresaSimuladaEmFoco().id,
    timeZone: 'America/Sao_Paulo',
    periodStart: from.toISOString(),
    periodEnd: to.toISOString(),
    points,
  };
}

function simularLeads(page: PageQuery): Page<LeadListItem> {
  const companyId = SESSAO_SIMULADA.company.id;
  const items: readonly LeadListItem[] = [
    { id: 'SIMULADO-lead-001', companyId, name: 'Mariana Ferraz', phoneE164: '+5511999880001', productName: 'Inglês Intensivo 12 meses', provider: 'kiwify', amountCents: 49_700, jobStatus: 'pending', tier: 'tier1_voice', voiceAttempts: 0, abandonedAt: menosMinutos(12), recoveredAt: null },
    { id: 'SIMULADO-lead-002', companyId, name: 'João Batista Lemos', phoneE164: '+5592988770002', productName: 'Mentoria de Conversação Pro', provider: 'hotmart', amountCents: 129_900, jobStatus: 'running', tier: 'tier1_voice', voiceAttempts: 1, abandonedAt: menosMinutos(46), recoveredAt: null },
    { id: 'SIMULADO-lead-003', companyId, name: 'Aline Souza Prado', phoneE164: '+5521977660003', productName: 'Imersão de Fluência (anual)', provider: 'kiwify', amountCents: 89_700, jobStatus: 'succeeded', tier: 'tier2_whatsapp', voiceAttempts: 2, abandonedAt: menosMinutos(180), recoveredAt: menosMinutos(131) },
    { id: 'SIMULADO-lead-004', companyId, name: 'Ricardo Nunes', phoneE164: '+5531966550004', productName: 'Espanhol do Zero', provider: 'kiwify', amountCents: 39_700, jobStatus: 'aborted', tier: 'tier1_voice', voiceAttempts: 0, abandonedAt: menosMinutos(240), recoveredAt: menosMinutos(228) },
    { id: 'SIMULADO-lead-005', companyId, name: 'Beatriz Camargo', phoneE164: '+5585955440005', productName: 'Inglês Intensivo 12 meses', provider: 'kiwify', amountCents: 49_700, jobStatus: 'failed', tier: 'exhausted', voiceAttempts: 2, abandonedAt: menosMinutos(300), recoveredAt: null },
    { id: 'SIMULADO-lead-006', companyId, name: null, phoneE164: '+5541944330006', productName: null, provider: 'hotmart', amountCents: 89_900, jobStatus: 'pending', tier: 'tier1_voice', voiceAttempts: 0, abandonedAt: menosMinutos(8), recoveredAt: null },
  ];
  return { items: items.slice(page.offset, page.offset + page.limit), total: items.length, limit: page.limit, offset: page.offset };
}

function simularCampanhas(page: PageQuery): Page<CampaignListItem> {
  const companyId = SESSAO_SIMULADA.company.id;
  const items: readonly CampaignListItem[] = [
    { id: 'SIMULADO-campanha-001', companyId, leadId: 'SIMULADO-lead-003', leadName: 'Aline Souza Prado', phoneE164: '+5521977660003', channel: 'voice', tier: 'tier1_voice', status: 'answered', amountCents: 89_700, converted: true, durationSeconds: 96, recordingUrl: gravacaoSimulada(6), errorMessage: null, createdAt: menosMinutos(150) },
    { id: 'SIMULADO-campanha-002', companyId, leadId: 'SIMULADO-lead-002', leadName: 'João Batista Lemos', phoneE164: '+5592988770002', channel: 'voice', tier: 'tier1_voice', status: 'voicemail', amountCents: 129_900, converted: false, durationSeconds: 14, recordingUrl: gravacaoSimulada(9), errorMessage: null, createdAt: menosMinutos(40) },
    { id: 'SIMULADO-campanha-003', companyId, leadId: 'SIMULADO-lead-005', leadName: 'Beatriz Camargo', phoneE164: '+5585955440005', channel: 'voice', tier: 'tier1_voice', status: 'no_answer', amountCents: 49_700, converted: false, durationSeconds: null, recordingUrl: null, errorMessage: null, createdAt: menosMinutos(295) },
    { id: 'SIMULADO-campanha-004', companyId, leadId: 'SIMULADO-lead-005', leadName: 'Beatriz Camargo', phoneE164: '+5585955440005', channel: 'whatsapp', tier: 'tier2_whatsapp', status: 'sent', amountCents: 49_700, converted: false, durationSeconds: null, recordingUrl: null, errorMessage: null, createdAt: menosMinutos(292) },
    { id: 'SIMULADO-campanha-005', companyId, leadId: 'SIMULADO-lead-004', leadName: 'Ricardo Nunes', phoneE164: '+5531966550004', channel: 'whatsapp', tier: 'tier2_whatsapp', status: 'failed', amountCents: 39_700, converted: false, durationSeconds: null, recordingUrl: null, errorMessage: 'Instância da Evolution API sem sessão ativa.', createdAt: menosMinutos(205) },
  ];
  return { items: items.slice(page.offset, page.offset + page.limit), total: items.length, limit: page.limit, offset: page.offset };
}

const CACHE_GRAVACOES = new Map<number, string>();

/** WAV de silencio montado em memoria, para exercitar o player sem rede. */
export function gravacaoSimulada(segundos: number): string {
  const emCache = CACHE_GRAVACOES.get(segundos);
  if (emCache !== undefined) {
    return emCache;
  }
  const taxa = 8_000;
  const amostras = taxa * segundos;
  const bytes: number[] = [];
  const texto = (t: string): void => {
    for (let i = 0; i < t.length; i += 1) {
      bytes.push(t.charCodeAt(i) & 0xff);
    }
  };
  const u32 = (v: number): void => {
    bytes.push(v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff);
  };
  const u16 = (v: number): void => {
    bytes.push(v & 0xff, (v >>> 8) & 0xff);
  };
  texto('RIFF');
  u32(36 + amostras);
  texto('WAVE');
  texto('fmt ');
  u32(16);
  u16(1);
  u16(1);
  u32(taxa);
  u32(taxa);
  u16(1);
  u16(8);
  texto('data');
  u32(amostras);
  for (let i = 0; i < amostras; i += 1) {
    bytes.push(128);
  }
  let binario = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binario += String.fromCharCode(bytes[i] ?? 0);
  }
  const uri = `data:audio/wav;base64,${window.btoa(binario)}`;
  CACHE_GRAVACOES.set(segundos, uri);
  return uri;
}
